import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { getAdminProducts } from '../../actions/adminProducts';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';

// --- Sub-components ---

const SearchBar = ({
  queryInput,
  parsedQuery,
  onInputChange,
  onSubmit,
  onReset,
  onNewProduct,
}: {
  queryInput: string;
  parsedQuery: string;
  onInputChange: (e: any) => void;
  onSubmit: (e: any) => void;
  onReset: () => void;
  onNewProduct: () => void;
}) => (
  <div className="admin-card-body" style={{ borderBottom: '1px solid #e1e3e5', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
    <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8, flex: 1 }}>
      <input
        className="form-control form-control-sm"
        placeholder="Search products…"
        value={queryInput}
        onChange={onInputChange}
        style={{ maxWidth: 300, fontSize: '0.82rem' }}
      />
    </form>
    {parsedQuery && (
      <span
        className="status-badge cancelled"
        style={{ cursor: 'pointer', fontSize: '0.75rem' }}
        onClick={onReset}
      >
        {parsedQuery} &times;
      </span>
    )}
    <button
      className="admin-dropdown-trigger"
      onClick={onNewProduct}
      type="button"
      style={{ background: '#ec174f', color: '#fff', borderColor: '#ec174f' }}
    >
      + New Product
    </button>
  </div>
);

const SortIcon = ({ filter, asc, desc }: { filter: string; asc: string; desc: string }) => {
  if (filter === asc) return <span style={{ marginLeft: 4 }}>↑</span>;
  if (filter === desc) return <span style={{ marginLeft: 4 }}>↓</span>;
  return <span style={{ marginLeft: 4, opacity: 0.3 }}>↕</span>;
};

const thStyle: React.CSSProperties = { cursor: 'pointer', userSelect: 'none' };

const ProductsTable = ({ products, filter, onRowClick, onSort }: { products: any[]; filter: string; onRowClick: (p: any) => void; onSort: (f: string) => void }) => (
  <div className="table-responsive">
    <table className="admin-table">
      <thead>
        <tr>
          <th>Image</th>
          <th>Name</th>
          <th style={thStyle} onClick={() => onSort(filter === 'inStock' ? 'OutofStock' : 'inStock')}>
            Stock <SortIcon filter={filter} asc="inStock" desc="OutofStock" />
          </th>
          <th style={thStyle} onClick={() => onSort(filter === 'pricelow' ? 'pricehigh' : 'pricelow')}>
            Price <SortIcon filter={filter} asc="pricelow" desc="pricehigh" />
          </th>
          <th style={thStyle} onClick={() => onSort(filter === 'newest' ? 'oldest' : 'newest')}>
            Date <SortIcon filter={filter} asc="newest" desc="oldest" />
          </th>
        </tr>
      </thead>
      <tbody>
        {products.map((item: any) => (
          <tr key={item._id} onClick={() => onRowClick(item)} style={{ background: item.inStock ? undefined : '#fffbeb' }}>
            <td style={{ width: 60 }}>
              <img src={item.image} alt={item.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
            </td>
            <td style={{ fontWeight: 600 }}>{item.name}</td>
            <td>
              <span className={`status-badge ${item.inStock ? 'confirmed' : 'cancelled'}`}>
                {item.inStock ? 'In Stock' : 'Out of Stock'}
              </span>
            </td>
            <td style={{ color: '#6d7175' }}>AUD {item.price.toFixed(2)}</td>
            <td className="order-time">{moment(item.lastUpdated).fromNow()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Pagination = ({
  currentPage,
  totalPages,
  onPrevious,
  onNext,
}: {
  currentPage: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
}) => (
  <div className="admin-card-body" style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
    <button
      className="admin-dropdown-trigger"
      onClick={onPrevious}
      disabled={currentPage <= 1}
      style={{ opacity: currentPage <= 1 ? 0.4 : 1 }}
    >
      Previous
    </button>
    <button
      className="admin-dropdown-trigger"
      onClick={onNext}
      disabled={currentPage >= totalPages}
      style={{ opacity: currentPage >= totalPages ? 0.4 : 1 }}
    >
      Next
    </button>
  </div>
);

// --- Main component ---

const ProductsDashboard = (props: any) => {
  const history = useHistory();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState(location.search);
  const [filter, setFilterState] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const searchQueryInitialized = useRef(false);
  const locationSearchInitialized = useRef(false);

  const updateSearchQuery = (parsed: any) => setSearchQuery('?' + queryString.stringify(parsed));

  useEffect(() => {
    const parsed = queryString.parse(location.search) as any;
    let page: any = 1, filterVal: any, query: any;

    if (parsed.page) page = parsed.page;
    if (parsed.filter) { filterVal = parsed.filter; setFilterState(filterVal); }
    if (parsed.query) { query = parsed.query; setQueryInput(''); }

    props.getAdminProducts(page, filterVal, query);
  }, []);

  useEffect(() => {
    if (!searchQueryInitialized.current) { searchQueryInitialized.current = true; return; }
    history.push(`/adminpanel/products${searchQuery}`);
  }, [searchQuery]);

  useEffect(() => {
    if (!locationSearchInitialized.current) { locationSearchInitialized.current = true; return; }
    const parsed = queryString.parse(location.search) as any;
    let page: any, filterVal: any, query: any;

    if (parsed.page) page = parsed.page;
    if (parsed.filter) filterVal = parsed.filter;
    if (parsed.query) query = parsed.query;

    updateSearchQuery(parsed);
    window.scrollTo(0, 0);
    props.getAdminProducts(page, filterVal, query);
  }, [location.search]);

  const setFilter = (value: string) => {
    const parsed = queryString.parse(location.search) as any;
    if (parsed.page) parsed.page = 1;
    parsed.filter = value;
    setFilterState(value);
    updateSearchQuery(parsed);
  };

  const handleReset = () => {
    setFilterState('');
    setSearchQuery('');
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const parsed = queryString.parse(location.search) as any;
    parsed.query = queryInput;
    parsed.page = undefined;
    setQueryInput('');
    updateSearchQuery(parsed);
  };

  const previousPage = () => {
    const parsed = queryString.parse(location.search) as any;
    parsed.page = parseInt(parsed.page as string) - 1;
    updateSearchQuery(parsed);
  };

  const nextPage = () => {
    const parsed = queryString.parse(location.search) as any;
    parsed.page = parsed.page ? parseInt(parsed.page as string) + 1 : 2;
    updateSearchQuery(parsed);
  };

  const handleProductClick = (product: any) => {
    history.push({
      pathname: `/adminpanel/products/${product.name.replace(/[^A-Z0-9]+/gi, '-')}/${product._id}`,
      state: { product },
    });
  };

  const parsed = queryString.parse(location.search) as any;
  const PAGE_SIZE = 12;
  const totalPages = Math.ceil(props.adminProducts.productCount / PAGE_SIZE);
  const currentPage = parsed.page
    ? parsed.page > totalPages ? totalPages : parsed.page <= 1 ? 1 : parseInt(parsed.page)
    : 1;
  const end = Math.min(PAGE_SIZE * currentPage, props.adminProducts.productCount);
  const startItem = currentPage <= 1 ? 1 : (currentPage - 1) * PAGE_SIZE + 1;

  return (
    <div>
      {props.adminProducts.loading ? (
        <div className="admin-empty-state">
          <i className="fas fa-spinner fa-spin" />
          <p>Loading products…</p>
        </div>
      ) : (
        <div className="admin-card">
          <div className="admin-card-header">
            <p className="admin-card-title">Products</p>
            {props.adminProducts.productCount > 0 && (
              <span style={{ fontSize: '0.78rem', color: '#6d7175' }}>
                {startItem}–{end} of {props.adminProducts.productCount}
              </span>
            )}
          </div>

          <SearchBar
            queryInput={queryInput}
            parsedQuery={parsed.query}
            onInputChange={(e) => setQueryInput(e.target.value)}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onNewProduct={() => history.push('/adminpanel/products/newProduct')}
          />

          {props.adminProducts.productCount === 0 ? (
            <div className="admin-empty-state">
              <i className="fas fa-box-open" />
              <p>No products found.</p>
            </div>
          ) : (
            <>
              <div className="admin-card-body-flush">
                <ProductsTable products={props.adminProducts.products} filter={filter} onRowClick={handleProductClick} onSort={setFilter} />
              </div>

              {totalPages > 1 && props.adminProducts.products.length !== 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPrevious={previousPage}
                  onNext={nextPage}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  adminProducts: state.adminProducts,
});

export default connect(mapStateToProps, { getAdminProducts })(ProductsDashboard);
